import React, { useState } from "react";
import ThemeModal from "./ThemeModal";
import { NavLink } from "react-router-dom";
import { useCustomerData, useUserRole } from "../../hooks/useAuth";
import { CreditCard, Database, GitCompareArrows, Mail, Printer, ShoppingCart, Shuffle, Store, Trash2, WalletCards, X } from "lucide-react";
import packageJson from "../../../package.json";
import { authStorage } from "../../utils/authStorage";
import { useTheme } from "./ThemeProvider";
import {
  HomeIcon,
  CubeIcon,
  Squares2X2Icon,
  PresentationChartBarIcon,
  DocumentTextIcon,
  UserGroupIcon,
  UserIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";


// -------------------- Role-based Menu Definition --------------------
const menuByRole = {
  0: [ // superadmin UI
    { name: "Dashboard", to: "/superadmin/dashboard", icon: HomeIcon },
    { name: "Customers", to: "/superadmin/customers", icon: UserGroupIcon },
    { name: "Add-on Requests", to: "/superadmin/add-on-requests", icon: CreditCard },
    { name: "Subscriptions", to: "/superadmin/subscription-settings", icon: CreditCard },
    { name: "Master Data", to: "/superadmin/master-data", icon: Database },
    { name: "Mail Config", to: "/superadmin/mail-config", icon: Mail },
    { name: "Payment Settings", to: "/superadmin/payment-settings", icon: WalletCards },
    { name: "Customer Printer Setup", to: "/superadmin/customer-printer-setup", icon: Printer },
    { name: "Customer Access Control", to: "/superadmin/customer-access-control", icon: ShieldCheckIcon },
  ],
  1: [ // admin UI
    { name: "Dashboard", to: "/admin/dashboard", icon: HomeIcon },
    { name: "Inventory", to: "/admin/inventory", icon: Squares2X2Icon },
    { name: "Inventory Logs", to: "/admin/inventory-logs", icon: Shuffle },
    { name: "Transactions", to: "/admin/transactions", icon: PresentationChartBarIcon },
    { name: "Expenses", to: "/admin/expenses", icon: DocumentTextIcon },
    { name: "Waste Requests", to: "/admin/waste-requests", icon: Trash2 },
    { name: "Products", to: "/admin/products", icon: CubeIcon },
    { name: "Price Logs", to: "/admin/product-price-logs", icon: GitCompareArrows },
    { name: "Users", to: "/admin/user-list", icon: UserIcon },
    { name: "Analytics", to: "/admin/analytics", icon: PresentationChartBarIcon },
    { name: "Reports", to: "/admin/reports", icon: DocumentTextIcon },
  ],
  2: [ // POS UI 
    { name: "Sales", to: "/dashboard", icon: ShoppingCart },
    { name: "Transactions", to: "/transactions", icon: PresentationChartBarIcon },
    { name: "Expenses", to: "/expenses", icon: DocumentTextIcon },
    { name: "Waste", to: "/waste", icon: Trash2 },
    { name: "Products Catalog", to: "/products", icon: CubeIcon },
  ],

  3: [ // POS UI 
    { name: "Sales", to: "/dashboard", icon: ShoppingCart },
    { name: "Transactions", to: "/transactions", icon: PresentationChartBarIcon },
    { name: "Expenses", to: "/expenses", icon: DocumentTextIcon },
    { name: "Waste", to: "/waste", icon: Trash2 },
    { name: "Products Catalog", to: "/products", icon: CubeIcon },
  ],
};

// -------------------- Mobile Header Toggle --------------------
// ...existing code...

// -------------------- Sidebar Navigation Links --------------------
function SidebarLinks({ menuItems, closeSidebar, onSettingsClick, isThemeModalOpen, collapsed = false, primaryColor }) {
  return (
    <nav
      className={`scrollbar-theme flex-1 overflow-y-auto pb-4 pt-2 ${collapsed ? "px-3 space-y-3" : "px-4 space-y-1.5"}`}
      data-sidebar-links
    >
      {menuItems.map(({ name, to, icon: Icon, isAppearance }) => {
        let navTo = to;
        return (
          <NavLink
            key={name}
            to={navTo}
            title={collapsed ? name : undefined}
            onClick={e => {
              if (isAppearance) {
                e.preventDefault();
                onSettingsClick("appearance");
                closeSidebar();
              } else {
                closeSidebar();
              }
            }}
            className={({ isActive }) => {
              let active = false;
              if (isThemeModalOpen && isAppearance) {
                active = true;
              } else {
                active = isActive;
              }
              return `group relative flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm transition-all duration-200 focus:outline-none focus:ring-4 ${
                active
                  ? "font-semibold shadow-[0_14px_30px_rgba(15,23,42,0.14)]"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-950"
              } ${collapsed ? "justify-center px-2" : ""}`;
            }}
            style={({ isActive }) => {
              let active = false;
              if (isThemeModalOpen && isAppearance) {
                active = true;
              } else {
                active = isActive;
              }
              return {
                background: active ? primaryColor : 'transparent',
                color: active ? '#ffffff' : undefined,
                boxShadow: active ? `0 16px 32px ${primaryColor}24` : undefined,
              };
            }}
          >
            {({ isActive }) => {
              let active = false;
              if (isThemeModalOpen && isAppearance) {
                active = true;
              } else {
                active = isActive;
              }
              return (
                <>
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition ${
                      active
                        ? "border-white/25 bg-white/15"
                        : "border-slate-200 bg-white text-slate-500 group-hover:border-slate-300 group-hover:text-slate-800"
                    }`}
                  >
                    <Icon
                      className="h-[18px] w-[18px]"
                      style={{ color: active ? '#ffffff' : undefined }}
                    />
                  </span>
                  {!collapsed && (
                    <span
                      className="min-w-0 flex-1 truncate"
                      style={{ color: active ? '#ffffff' : undefined }}
                    >
                      {name}
                    </span>
                  )}
                </>
              );
            }}
          </NavLink>
        );
      })}
    </nav>
  );
}

function WorkspaceHeader({ role, customer, closeSidebar, collapsed = false, primaryColor }) {
  const roleConfig = {
    0: {
      label: "ZeroOne Platform",
      subtitle: "Super Admin",
      icon: ShieldCheckIcon,
    },
    1: {
      label: customer?.name || "Store Workspace",
      subtitle: "Admin",
      icon: Store,
    },
    2: {
      label: customer?.name || "Point of Sale",
      subtitle: "Cashier",
      icon: Store,
    },
    3: {
      label: customer?.name || "Point of Sale",
      subtitle: "Staff",
      icon: Store,
    },
  };
  const meta = roleConfig[role] || roleConfig[1];
  const Icon = meta.icon;

  if (collapsed) {
    return (
      <div className="px-3 pb-5 pt-5">
        <div
          className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-[0_14px_30px_rgba(15,23,42,0.16)]"
          style={{ background: primaryColor }}
        >
          <Icon className="h-6 w-6" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pb-4 pt-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3 rounded-2xl border border-slate-200 bg-white/90 px-3 py-3 shadow-[0_12px_28px_rgba(15,23,42,0.06)]">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm"
            style={{ background: primaryColor }}
          >
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-slate-950">{meta.label}</p>
            <p className="truncate text-xs text-slate-500">{meta.subtitle}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={closeSidebar}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 lg:hidden"
          aria-label="Close sidebar"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

// -------------------- Main Sidebar Component --------------------
export default function Sidebar({ closeSidebar, onBrandUpdated, collapsed = false }) {
  const [isThemeModalOpen, setThemeModalOpen] = useState(false);
  const { currentTheme } = useTheme();
  const primaryColor = currentTheme?.color || "var(--color-primary)";
  const role = useUserRole();
  const getCustomerData = useCustomerData();
  const storedUser = authStorage.getUser();
  const customer = (role === 1 || role === 2 || role === 3) ? getCustomerData() : null;
  const isManager = storedUser?.user_type === 2
    && String(storedUser?.role_name || "").toLowerCase() === "manager";
  const managerHiddenItems = new Set(["User List", "Branches", "Pre Release Tracker"]);
  const menuItems = [...(menuByRole[role] || [])]
    .filter(item => !(isManager && managerHiddenItems.has(item.name)));

  const handleModalOpen = (type) => {
    if (type === "appearance") {
      setThemeModalOpen(true);
    }
  };
  const handleModalClose = (type) => {
    if (type === "appearance") {
      setThemeModalOpen(false);
    }
  };

  return (
    <>
      <div className="relative flex h-full flex-col overflow-hidden border-r border-slate-200/80 bg-gradient-to-b from-white via-white to-slate-50 text-slate-700">
        <WorkspaceHeader
          role={role}
          customer={customer}
          closeSidebar={closeSidebar}
          collapsed={collapsed}
          primaryColor={primaryColor}
        />
        <SidebarLinks
          menuItems={menuItems}
          closeSidebar={closeSidebar}
          onSettingsClick={handleModalOpen}
          isThemeModalOpen={isThemeModalOpen}
          collapsed={collapsed}
          primaryColor={primaryColor}
        />
        {!collapsed && (
          <div className="mt-auto border-t border-slate-200/80 bg-white/80 px-4 py-3">
            <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-3 py-2 text-xs text-slate-500">
              <span className="font-medium">Version</span>
              <span className="font-semibold text-slate-700">{packageJson.version}</span>
            </div>
          </div>
        )}
      </div>
      <ThemeModal isOpen={isThemeModalOpen} onClose={() => handleModalClose("appearance")} />
    </>
  );
}
